import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Divider, Link } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { RouteData } from '../../types';
import { useCyclingToasts } from '../../hooks/useCyclingToasts';
import { RouteSummary } from '../common/RouteSummary';
import { Link as RouterLink } from 'react-router-dom';

interface DownloadGpxModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void; // triggers actual GPX generation/download
  disabled?: boolean; // disable if route invalid
  routeData?: RouteData; // metrics summary
  bikeProfileName?: string;
}

// NOTE: we now rely on shared formatting utilities for consistency

export const DownloadGpxModal: React.FC<DownloadGpxModalProps> = ({ open, onClose, onConfirm, disabled = false, routeData, bikeProfileName }) => {
  const cyclingToasts = useCyclingToasts();
  const handleConfirm = () => {
    try {
      onConfirm();
      cyclingToasts.routeExported('GPX');
      onClose();
    } catch (e) {
      cyclingToasts.showError('Failed to export GPX. Please try again.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          <Typography variant="subtitle1" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DownloadIcon color="primary" fontSize="small" />
            Export GPX
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Download this route as a GPX file. You can import it into most navigation or training apps.
        </Typography>

        <Divider sx={{ my: 1.5 }} />

        {routeData && (
          <RouteSummary
            distance={routeData.distance}
            duration={routeData.duration}
            elevationGain={routeData.ascend}
            bikeProfile={bikeProfileName}
          />
        )}

        <Box display="flex" alignItems="center" gap={1} mt={1}>
          <DescriptionIcon fontSize="small" color="action" />
          <Typography variant="caption" color="text.secondary">
            File format: .gpx (UTF-8, includes elevation & route points)
          </Typography>
        </Box>

        <Box
          sx={{
            mt: 3,
            textAlign: 'center',
            backgroundColor: 'rgba(196, 227, 194, 0.4)',
            borderRadius: 1.5,
            px: 2,
            py: 1.5,
            border: '1px solid rgba(14, 44, 38, 0.2)'
          }}
        >
          <Typography
            variant="body2"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.75,
              color: 'var(--color-dark)',
              fontSize: '0.75rem'
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'info.main' }} />
            Quick heads-up: double-check your route and local rules before you ride.
          </Typography>
          <Link
            component={RouterLink}
            to="/ride-responsibly"
            onClick={() => {
              window.open('/ride-responsibly', '_blank');
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              mt: 0.75,
              fontSize: '0.75rem',
              fontWeight: 600,
              color: 'info.main',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            Before You Ride – Important Info
          </Link>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Button onClick={onClose} variant="outlined">Cancel</Button>
        {/* Keep a primary action for accessibility even though summary has export */}
        <Button
          onClick={handleConfirm}
          variant="contained"
          startIcon={<DownloadIcon />}
          disabled={disabled}
          color="primary"
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DownloadGpxModal;
